import { useEffect, useState } from 'react'
import { FileDiff } from 'lucide-react'
import type { ClientRateconRevision } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'
import { useAppDispatch } from '@/hooks'
import { rateconRevisionsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ratecon-revisions.api'
import {
    isAlreadyResolvedError,
    RateconRevisionDiffTable,
    RateconRevisionDocumentNotice,
    RateconRevisionKindBadges,
} from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/components/RateconRevisionDiff'
import { buildDiffRows, revisionApplyLabel } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/components/RateconRevisionModal'
import {
    RateconRevisionRouteComparison,
    RateconRevisionRouteShapeNotice,
} from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/components/RateconRevisionRouteComparison'
import { updateSingleLoad } from '@/store/loadsSlice'
import { getServerErrorMessage } from '@/utils/get-user-error-message'
import type { OpenedRevision, RevisionChoice } from './uploadFlow'

interface RevisionCardProps {
    revision: OpenedRevision
    onResolved: (choice: RevisionChoice) => void
    /** Told when an accept or a keep starts and ends, so the panel holding the card is not closed under it. */
    onSubmittingChange?: (submitting: boolean) => void
}

/**
 * A rate con from the chat that the board filed as a revision of a load it already had: the board's own diff, notices and
 * choices (accept the revision or keep the original), through the board's revision routes and their permissions, so the
 * load changes only as Ongoing Loads' review would change it.
 */
export const RevisionCard = ({ revision, onResolved, onSubmittingChange }: RevisionCardProps) => {
    const dispatch = useAppDispatch()
    const [review, setReview] = useState<ClientRateconRevision | null>(null)
    const [loadFailed, setLoadFailed] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let current = true
        rateconRevisionsApi.get(revision.revisionId).then(
            (found) => current && setReview(found),
            () => current && setLoadFailed(true)
        )
        return () => {
            current = false
        }
    }, [revision.revisionId])

    const resolveWith = async (action: () => Promise<RevisionChoice>, failureMessage: string) => {
        setSubmitting(true)
        onSubmittingChange?.(true)
        setError(null)
        try {
            onResolved(await action())
        } catch (err) {
            if (isAlreadyResolvedError(err)) onResolved({ kind: 'gone' })
            else setError(getServerErrorMessage(err) ?? failureMessage)
        } finally {
            setSubmitting(false)
            onSubmittingChange?.(false)
        }
    }

    const accept = () =>
        resolveWith(async () => {
            const { load, warning } = await rateconRevisionsApi.accept(revision.revisionId)
            // As the board's review does: the loads slice shows the accepted load at once.
            dispatch(updateSingleLoad(load))
            return { kind: 'accepted', load, warning }
        }, 'Failed to accept revision. Please try again.')

    const keepOriginal = () =>
        resolveWith(async () => {
            await rateconRevisionsApi.reject(revision.revisionId)
            return { kind: 'kept' }
        }, 'Failed to keep original. Please try again.')

    return (
        <section aria-label={`Revision of load #${revision.loadNumber}`} className="flex min-w-0 flex-col gap-3 text-sm text-foreground">
            <div className="flex flex-wrap items-center gap-2">
                <FileDiff className="h-4 w-4 text-foreground-muted" aria-hidden />
                <h3 className="font-semibold">This rate con is a revision of load #{revision.loadNumber}</h3>
                <div className="flex-1" />
                {review && <RateconRevisionKindBadges revision={review} />}
            </div>
            <span className="text-xs text-foreground-muted">{revision.fileName}</span>
            {loadFailed && (
                <p role="alert" className="text-danger-soft-foreground">
                    The revision could not be loaded. Review it on Ongoing Loads.
                </p>
            )}
            {!review && !loadFailed && <p className="text-foreground-muted">Loading the changes…</p>}
            {review && (
                <>
                    <RateconRevisionDiffTable revision={review} rows={buildDiffRows(review)} compact />
                    <RateconRevisionDocumentNotice revision={review} />
                    {review.routeShapeChanged && (
                        <div className="flex flex-col gap-3">
                            <RateconRevisionRouteShapeNotice revision={review} />
                            <div className="max-h-[264px] overflow-y-auto">
                                <RateconRevisionRouteComparison v1Route={review.v1.route} v2Route={review.v2.route} compact />
                            </div>
                        </div>
                    )}
                    {revision.invoiced && (
                        <p className="text-foreground-muted">
                            Load #{revision.loadNumber} is already invoiced: accepting decides what to bill, so accept it on{' '}
                            <a
                                className="text-accent underline"
                                href={`/updater-agent/loads/${encodeURIComponent(revision.loadNumber)}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Ongoing Loads
                            </a>
                            .
                        </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        {!revision.invoiced && (
                            <Button size="sm" loading={submitting} disabled={submitting} onClick={() => void accept()}>
                                {revisionApplyLabel(review)}
                            </Button>
                        )}
                        <Button size="sm" variant="secondary" disabled={submitting} onClick={() => void keepOriginal()}>
                            Keep original
                        </Button>
                        <span className="ml-auto text-xs text-foreground-muted">updates the load · logs Timeline</span>
                    </div>
                </>
            )}
            {error && (
                <p role="alert" className="text-danger-soft-foreground">
                    {error}
                </p>
            )}
        </section>
    )
}
